import React, { useState, useEffect, useContext, useMemo } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'
import BsButton from 'react-bootstrap/Button'
import { toast } from 'react-toastify'
import UserContext from 'context/user-context'
import ComposeAnalyticModal from '../policies/ComposeAnalyticModal'
import Page from 'layout/page'
import PageTitle from 'layout/page-title'
import { split, groupBy } from 'lodash'
import WeightForm from './WeightForm'
import AnalyticDescription from 'analytics/description'
import FlagCount from 'shared/flag-count'
import TransactionTable from 'transactions/table'
import { useAnalytic } from 'api/hooks/use-analytics'
import EmptyPage from 'layout/empty-page'
import { useQueryClient } from 'react-query'
import { useUpdateMunicipalityAnalytic } from 'api/hooks/use-municipality-analytics'
import StatusAlert from './StatusAlert'
import ImportProgressAlert from './ImportProgressAlert'
import NewView from 'dashboard/new'
import ConfigEditor from './ConfigEditor'
import RunAnalyticButton from './RunAnalyticButton'
import Badge from 'react-bootstrap/Badge'
import Modal from 'react-bootstrap/Modal'
import Spinner from 'react-bootstrap/Spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBookOpen, faPen } from '@fortawesome/free-solid-svg-icons'
import { useAnalyticPolicyRules } from 'api/hooks/use-analytic-policy-rules'

const CONFIDENCE_THRESHOLD = 0.65

const LinkedPolicyRulesModal = ({ onHide, analyticId }) => {
  const { data: rawData, isLoading } = useAnalyticPolicyRules({ analyticId })

  const data = (rawData || []).filter((pra) => pra.confidence >= CONFIDENCE_THRESHOLD)

  const byDocument = groupBy(data, (pra) => pra.policy_rule.document_name)

  return (
    <Modal show centered size="lg" onHide={onHide} scrollable>
      <Modal.Header closeButton>
        <div>
          <Modal.Title className="fs-6 fw-bold">
            <FontAwesomeIcon icon={faBookOpen} className="me-2" />
            Linked Policy Rules {!isLoading && `(${data.length})`}
          </Modal.Title>
          <small className="text-muted">
            Manage policy links in <a href="/policies">Policies</a>
          </small>
        </div>
      </Modal.Header>
      <Modal.Body className="p-0">
        {isLoading ? (
          <div className="p-4 text-center">
            <Spinner animation="border" size="sm" />
          </div>
        ) : data.length === 0 ? (
          <div className="px-3 py-3 text-muted small">No linked policy rules.</div>
        ) : (
          Object.entries(byDocument).map(([docName, rules], docIdx, docArr) => (
            <div key={docName}>
              <div
                className="px-3 py-2 fw-semibold small text-muted"
                style={{ background: '#f8f9fa', borderBottom: '1px solid #e9ecef', letterSpacing: '0.04em' }}
              >
                {docName}
              </div>
              {rules.map((pra, i) => (
                <div
                  key={pra.id}
                  className="px-3 py-3"
                  style={{ borderBottom: i < rules.length - 1 || docIdx < docArr.length - 1 ? '1px solid #e9ecef' : 'none' }}
                >
                  <div className="d-flex align-items-center mb-1">
                    <span className="fw-semibold small">{pra.policy_rule.summary || pra.policy_rule.category}</span>
                    <Badge bg="" className="badge-warning ms-2" style={{ fontSize: '0.65rem' }}>
                      {Math.round(pra.confidence * 100)}%
                    </Badge>
                  </div>
                  {pra.policy_rule.content && (
                    <blockquote
                      className="mb-0 ps-3 small text-muted"
                      style={{ borderLeft: '3px solid #dee2e6', fontStyle: 'italic' }}
                    >
                      {pra.policy_rule.content}
                    </blockquote>
                  )}
                </div>
              ))}
            </div>
          ))
        )}
      </Modal.Body>
    </Modal>
  )
}

const LinkedPolicyRules = ({ analyticId }) => {
  const { data, isLoading } = useAnalyticPolicyRules({ analyticId })
  const [showModal, setShowModal] = useState(false)

  if (isLoading) {
    return (
      <div className="mt-3 d-flex align-items-center gap-1 text-muted">
        <Spinner animation="border" size="sm" style={{ width: '0.6rem', height: '0.6rem' }} />
        <small>Loading policy rules...</small>
      </div>
    )
  }

  const count = (data || []).filter((pra) => pra.confidence >= CONFIDENCE_THRESHOLD).length

  return (
    <>
      <div
        className="d-inline-flex align-items-center gap-2 px-2 py-1 rounded border small text-muted"
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setShowModal(true)}
        role="button"
      >
        <FontAwesomeIcon icon={faBookOpen} />
        {count > 0
          ? `Linked to ${count} ${count === 1 ? 'policy rule' : 'policy rules'}`
          : 'Link policy rules'}
      </div>
      {showModal && <LinkedPolicyRulesModal analyticId={analyticId} onHide={() => setShowModal(false)} />}
    </>
  )
}

function Analytic() {
  const queryString = window.location.pathname
  const queryClient = useQueryClient()
  const analyticId = split(queryString, '/')[2]
  const params = {
    include_chart: true,
    id: analyticId
  }

  const [polling, setPolling] = useState(false)
  const [pendingWeightChange, setPendingWeightChange] = useState(false)
  const [showEditRecipe, setShowEditRecipe] = useState(false)
  const { municipality_id, is_admin } = useContext(UserContext)

  const { isSuccess, isLoading, data: analytic } = useAnalytic({
    params,
    options: { refetchInterval: polling ? 2000 : false }
  })

  const isServerBusy = analytic?.municipality_analytic?.status === 'processing' || analytic?.municipality_analytic?.status === 'queued'
  const isBusy = isServerBusy || pendingWeightChange
  const isPCard = analytic?.section_name === 'PCard'
  const isPurchasing = ['Purchase Orders', 'Requisitions'].includes(analytic?.section_name)
  const isAccountsPayable = analytic?.section_name === 'Accounts Payable'
  // Both vendor sections: 'Vendor' (the vendor audit log) and 'VO' (the
  // vendor master). Matched on abbreviation because display names differ
  // between environments.
  const isVendor = ['Vendor', 'VO'].includes(analytic?.section_abbreviation)
  const isCardholder = analytic?.section_name === 'Cardholder'
  // Custom analytics are always considered configurable — the recipe is
  // their config.
  const isCustom = !!analytic?.custom
  // Fixed Usage is a config-supported section: some of its analytics are
  // tunable, so even the param-less ones (work-order / zero-usage) should show
  // the config panel's "no configurable parameters" message rather than the
  // section-level "coming soon".
  const isFixedUsage = analytic?.section_name === 'Utility Billing Fixed Usage'
  // An analytic with mapped tunable params is configurable regardless of
  // section — this also covers newly-wired core analytics without a hardcoded
  // section allowlist entry — as long as it can be run on demand, since a run
  // is how a changed value is previewed and saved. `runs_on_demand` is null
  // when that isn't known, which keeps the analytic configurable.
  const hasConfigParams = (analytic?.config_keys || []).length > 0 && analytic?.runs_on_demand !== false
  const isConfigurable = isPCard || isPurchasing || isAccountsPayable || isVendor ||
                         isCardholder || isFixedUsage || isCustom || hasConfigParams

  const recipeSections = useMemo(() => ([{
    id: analytic?.section_id,
    name: analytic?.section_name,
    abbreviation: analytic?.section_abbreviation
  }]), [analytic?.section_id, analytic?.section_name, analytic?.section_abbreviation])

  const wasBusyRef = React.useRef(false)

  useEffect(() => {
    setPolling(isBusy)

    if (isBusy) {
      setConfigChanged(false)
      wasBusyRef.current = true
    } else if (wasBusyRef.current) {
      wasBusyRef.current = false
      queryClient.invalidateQueries(['transactions'])
    }
  }, [isBusy, queryClient])

  useEffect(() => {
    if (!pendingWeightChange) return

    if (isServerBusy) {
      setPendingWeightChange(false)
      return
    }

    const timeout = setTimeout(() => setPendingWeightChange(false), 10000)
    return () => clearTimeout(timeout)
  }, [pendingWeightChange, isServerBusy])

  const crumbs = [{ name: 'Analytics', url: 'analytics' }]

  const [activeBadge, setActiveBadge] = useState('')
  const [configOverrides, setConfigOverrides] = useState({})
  const [configChanged, setConfigChanged] = useState(false)

  useEffect(() => {
    if (isSuccess) {
      setActiveBadge(analytic.municipality_analytic?.weight)
    }
  }, [isSuccess, setActiveBadge, analytic])

  const { mutate } = useUpdateMunicipalityAnalytic({
    options: {
      onMutate: () => {
        setPendingWeightChange(true)
      },
      onSuccess: () => {
        queryClient.invalidateQueries(['analytic'])
        queryClient.invalidateQueries(['monitoring-analytics'])
        queryClient.invalidateQueries(['monitoring-control-analytics'])
        queryClient.invalidateQueries(['analytics-monitoring'])
        queryClient.invalidateQueries(['analytics-monitoring-transactions'])
        queryClient.invalidateQueries(['monitoring-high-risk-transactions'])
      }
    }
  })

  useEffect(() => {
    if (
      isSuccess &&
      activeBadge !== analytic?.municipality_analytic?.weight &&
      activeBadge !== ''
    ) {
      mutate({
        id: analytic?.municipality_analytic?.id,
        weight: activeBadge
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBadge, analytic?.municipality_analytic?.weight, isSuccess])

  if (isLoading) {
    return <EmptyPage />
  }

  return (
    <Page
      title={analytic.name.length > 67 ? `${analytic.name.slice(0, 67)}...` : analytic.name}
      pageTitle={`Analytic - ${analytic.name}`}
      subtitle={
        <span className="d-inline-flex align-items-center gap-2">
          <FlagCount count={analytic.analytic_count} />
          <NewView
            sectionId={analytic.section_id}
            sectionName={analytic.section_name}
            initialAnalytics={[
              {
                id: analytic.id,
                name: analytic.name,
                description: analytic.description,
                options: 'include'
              }
            ]}
            initialParams={{
              name: analytic.name,
              description: analytic.description
            }}
            size="sm"
          />
        </span>
      }
    >
      {isSuccess && (
        <React.Fragment>
          <PageTitle breadcrumbs={crumbs} />
          <StatusAlert status={analytic.municipality_analytic?.status} />
          <Row>
            <Col sm={6}>
              <AnalyticDescription analytic={analytic} />
            </Col>

            <Col sm={6}>
              <Card>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="fw-bold">Policies</div>
                    <LinkedPolicyRules analyticId={analytic.id} />
                  </div>
                  <hr className="text-muted opacity-25" />
                  <div className="mb-3">
                    <WeightForm
                      activeBadge={activeBadge}
                      setActiveBadge={setActiveBadge}
                      disabled={isBusy}
                    />
                  </div>
                  <hr className="text-muted opacity-25" />
                  {isConfigurable && configChanged && !isBusy && (
                    <div
                      className="mb-3 px-3 py-2 rounded small"
                      style={{
                        borderLeft: '4px solid var(--bs-info)',
                        backgroundColor: 'var(--bs-info-bg-subtle, #cff4fc)',
                        color: 'var(--bs-info-text-emphasis, #055160)'
                      }}
                    >
                      Configuration has been changed. Run the analytic to apply.
                    </div>
                  )}
                  {!isConfigurable && (
                    <div
                      className="mb-3 px-3 py-2 rounded"
                      style={{
                        borderLeft: '4px solid var(--bs-warning)',
                        backgroundColor: 'var(--bs-warning-bg-subtle, #fff3cd)',
                        color: 'var(--bs-warning-text-emphasis, #664d03)'
                      }}
                    >
                      <span className="fw-semibold">Analytic configuration for {analytic.section_name} coming soon</span>
                    </div>
                  )}
                  {isCustom ? (
                    // Custom analytics: the recipe IS the configuration, so the
                    // tunable-params form is replaced by the recipe entry point.
                    // The plain-English explanation lives in the Description
                    // card; the full definition is behind Edit recipe.
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="fw-bold">How this analytic works</div>
                        <BsButton
                          variant="light"
                          className="text-primary"
                          size="sm"
                          onClick={() => setShowEditRecipe(true)}
                          disabled={isBusy}
                        >
                          <FontAwesomeIcon icon={faPen} className="me-1" />
                          Edit recipe
                        </BsButton>
                      </div>
                      <div className="small text-muted mt-2">
                        The description explains this analytic in plain English. Edit the
                        recipe to see the full definition and change what it flags.
                      </div>
                    </div>
                  ) : (
                    <ConfigEditor
                      analyticId={analytic.id}
                      onConfigChange={setConfigOverrides}
                      onHasChanges={setConfigChanged}
                      disabled={!isConfigurable || isBusy}
                    />
                  )}
                  <ImportProgressAlert municipalityAnalytic={analytic.municipality_analytic} expectingWork={pendingWeightChange} />
                  {/* Shown whether or not the config was changed, so a one-off
                      run can be kicked off against the current configuration.
                      Admins can also run analytics that aren't configurable. */}
                  {(isConfigurable || is_admin) && !isBusy && (
                    <RunAnalyticButton
                      analyticId={analytic.id}
                      configOverrides={configChanged ? configOverrides : {}}
                      configChanged={configChanged}
                      currentCount={analytic.analytic_count}
                    />
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {isCustom && showEditRecipe && (
            <ComposeAnalyticModal
              show={showEditRecipe}
              onHide={() => setShowEditRecipe(false)}
              municipalityId={municipality_id}
              analyticId={analytic.id}
              sections={recipeSections}
              defaultSectionId={analytic.section_id}
              onSaved={() => {
                setShowEditRecipe(false)
                queryClient.invalidateQueries(['analytic'])
                // The ConfigEditor's tunable-param list is keyed on this exact
                // analytic id (see useAnalyticConfigKeys) and won't refetch on
                // its own — without this it keeps showing the pre-edit params
                // until a hard reload.
                queryClient.invalidateQueries(['analytic_config_keys', analytic.id])
                toast.success('Recipe updated. Run the analytic to apply the changes.')
              }}
            />
          )}

          <Row className="mt-4">
            <Col>
              <TransactionTable
                analytic={[analytic]}
                sectionId={analytic.section_id}
                filtersEnabled={false}
                downloadButton={false}
                source={analytic.name}
                columns={(() => {
                  const dynamicColumns = (analytic.table_columns || []).map(tc =>
                    tc.database_column_name === 'vendor_id' ? 'vendor' : tc.database_column_name
                  )
                  return [
                    'id',
                    ...(dynamicColumns.length > 0
                      ? dynamicColumns
                      : ['vendor', 'employee', 'transaction_on', 'line_amount']
                    ),
                    'risk_score',
                    'analytics_count'
                  ]
                })()}
                title="Transactions for Analytic"
              />
            </Col>
          </Row>
        </React.Fragment>
      )}
    </Page>
  )
}

Analytic.propTypes = {}

export default Analytic
